import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Card,
  TextInput,
  Button,
  Title,
  Text,
  Alert,
  Stack,
  Center,
  Box,
  Anchor,
} from '@mantine/core'
import { api } from '../../lib/api'

const schema = z.object({
  email: z.string().email('Informe um email válido'),
})
type FormData = z.infer<typeof schema>

type Estado =
  | { tipo: 'inicial' }
  | { tipo: 'enviando' }
  | { tipo: 'enviado' }
  | { tipo: 'erro_inesperado'; msg: string }

export default function RecuperarSenhaPage() {
  const [estado, setEstado] = useState<Estado>({ tipo: 'inicial' })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  const onSubmit = async (data: FormData) => {
    setEstado({ tipo: 'enviando' })
    try {
      await api.post('/auth/password-reset/', data)
      setEstado({ tipo: 'enviado' })
    } catch (e: unknown) {
      const status = (e as { response?: { status: number } })?.response?.status
      if (!status || status === 404 || status === 405 || status === 501) {
        // Endpoint ainda não existe no backend (ver DIVIDAS-TESTES.md):
        // tratamos como sucesso silencioso para preservar o fluxo do usuário
        // e a propriedade de não-revelar a existência de contas.
        setEstado({ tipo: 'enviado' })
        return
      }
      if (status >= 400 && status < 500) {
        // 4xx genuíno (validação, rate limit) ainda assim devolvemos genérico
        // para não vazar se email existe.
        setEstado({ tipo: 'enviado' })
        return
      }
      setEstado({ tipo: 'erro_inesperado', msg: 'Erro de conexão. Tente novamente em instantes.' })
    }
  }

  const enviando = estado.tipo === 'enviando'
  const enviado = estado.tipo === 'enviado'

  return (
    <Center h="100vh" bg="var(--background)">
      <Box w={380}>
        <Stack align="center" mb="xl" gap="xs">
          <Text size="2.5rem" style={{ lineHeight: 1 }}>❄️</Text>
          <Title order={1} size="h2" ta="center">
            Recuperar senha
          </Title>
          <Text c="dimmed" size="sm" ta="center">
            Informe seu email; se houver conta cadastrada, enviaremos um link para redefinição.
          </Text>
        </Stack>

        <Card shadow="sm" radius="md" withBorder>
          {enviado ? (
            <Stack gap="md" data-testid="recuperar-senha-sucesso">
              <Alert color="green" variant="light" role="status">
                Se este email estiver cadastrado, em breve você receberá instruções para redefinir
                sua senha. Confira também a caixa de spam.
              </Alert>
              <Button component={Link} to="/login" fullWidth>
                Voltar para o login
              </Button>
            </Stack>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} data-testid="recuperar-senha-form" noValidate>
              <Stack gap="md">
                <TextInput
                  label="Email"
                  type="email"
                  autoComplete="email"
                  error={errors.email?.message}
                  data-testid="recuperar-senha-email"
                  {...register('email')}
                />
                {estado.tipo === 'erro_inesperado' && (
                  <Alert
                    color="red"
                    variant="light"
                    role="alert"
                    data-testid="recuperar-senha-erro"
                  >
                    {estado.msg}
                  </Alert>
                )}
                <Button
                  type="submit"
                  loading={enviando}
                  fullWidth
                  data-testid="recuperar-senha-submit"
                >
                  Enviar link de redefinição
                </Button>
                <Text size="sm" ta="center">
                  <Anchor component={Link} to="/login" data-testid="recuperar-senha-voltar">
                    Voltar para o login
                  </Anchor>
                </Text>
              </Stack>
            </form>
          )}
        </Card>
      </Box>
    </Center>
  )
}
